import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import type { ScenechatAccountRow, ScenechatSuggestionRow, ScenechatCampaignRow, ScenechatVoteOptionRow } from '../lib/database.types';

export interface Account {
  id: string;
  displayName: string;
  handle: string;
  instagramUrl: string;
  profileImageUrl: string | null;
  qrCodePath: string | null;
  qrCodePublicUrl: string | null;
  publicPageUrl: string | null;
  closesAt: string | null;
  theme: Record<string, string> | null;
  createdAt: string;
  suggestions: Suggestion[];
}

export interface Suggestion {
  id: string;
  message: string;
  submittedBy: string | null;
  used: boolean;
  source: 'qr' | 'direct';
  campaignId: string | null;
  location: string | null;
  createdAt: string;
}

export interface Campaign {
  id: string;
  accountId: string;
  slug: string;
  type: 'poster' | 'debate' | 'vote';
  title: string;
  description: string | null;
  closesAt: string | null;
  createdAt: string;
}

export interface VoteOption {
  id: string;
  campaignId: string;
  label: string;
  sortOrder: number;
}

interface AppStore {
  currentAccount: Account | null;
  hasFollowed: boolean;
  hasSubmitted: boolean;
  realtimeChannel: any;

  // Auth (code-based)
  loginWithCode: (code: string) => Promise<boolean>;
  logout: () => void;

  // Account
  fetchAccountByHandle: (handle: string) => Promise<Account | null>;
  updateAccount: (accountId: string, updates: Partial<Account>) => Promise<void>;

  // Suggestions
  addSuggestion: (accountId: string, message: string, submittedBy: string | null, source: 'qr' | 'direct', campaignId?: string, location?: string | null) => Promise<void>;
  toggleUsed: (suggestionId: string) => Promise<void>;
  removeAllSuggestions: () => Promise<void>;
  refreshSuggestions: () => Promise<void>;

  // Instagram gate
  setHasFollowed: (value: boolean) => void;

  // Realtime
  subscribeToSuggestions: () => void;
  unsubscribeFromSuggestions: () => void;

  // QR
  generateAndStoreQR: (accountId: string) => Promise<void>;

  // Campaigns
  fetchCampaign: (handle: string, slug: string) => Promise<{ campaign: Campaign; account: Account } | null>;
  fetchCampaignsByAccount: (accountId: string) => Promise<Campaign[]>;
  createCampaign: (accountId: string, data: Omit<Campaign, 'id' | 'accountId' | 'createdAt'>) => Promise<Campaign>;
  updateCampaign: (campaignId: string, updates: Partial<Pick<Campaign, 'title' | 'description' | 'closesAt' | 'slug'>>) => Promise<void>;
  deleteCampaign: (campaignId: string) => Promise<void>;
  fetchVoteOptions: (campaignId: string) => Promise<VoteOption[]>;
  saveVoteOptions: (campaignId: string, labels: string[]) => Promise<void>;
  fetchCampaignSuggestions: (campaignId: string) => Promise<Suggestion[]>;
  deleteSuggestion: (suggestionId: string) => Promise<void>;
  deleteCampaignSuggestions: (campaignId: string) => Promise<void>;
  toggleSuggestionUsed: (suggestionId: string, currentUsed: boolean) => Promise<void>;
}

function mapRowToAccount(row: ScenechatAccountRow, suggestions: ScenechatSuggestionRow[] = []): Account {
  return {
    id: row.id,
    displayName: row.display_name,
    handle: row.handle,
    instagramUrl: row.instagram_url,
    profileImageUrl: row.profile_image_url,
    qrCodePath: row.qr_code_path,
    qrCodePublicUrl: row.qr_code_public_url,
    publicPageUrl: row.public_page_url,
    closesAt: row.closes_at,
    theme: row.theme,
    createdAt: row.created_at,
    suggestions: suggestions.map(mapRowToSuggestion),
  };
}

function mapRowToSuggestion(row: ScenechatSuggestionRow): Suggestion {
  return {
    id: row.id,
    message: row.message,
    submittedBy: row.submitted_by,
    used: row.used,
    source: row.source as 'qr' | 'direct',
    campaignId: row.campaign_id,
    location: row.location ?? null,
    createdAt: row.created_at,
  };
}

function mapRowToCampaign(row: ScenechatCampaignRow): Campaign {
  return {
    id: row.id,
    accountId: row.account_id,
    slug: row.slug,
    type: row.type,
    title: row.title,
    description: row.description,
    closesAt: row.closes_at,
    createdAt: row.created_at,
  };
}

function mapRowToVoteOption(row: ScenechatVoteOptionRow): VoteOption {
  return {
    id: row.id,
    campaignId: row.campaign_id,
    label: row.label,
    sortOrder: row.sort_order,
  };
}

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      currentAccount: null,
      hasFollowed: false,
      hasSubmitted: false,
      realtimeChannel: null,

      loginWithCode: async (code: string) => {
        const handle = code.trim().toLowerCase();
        const { data: row, error } = await supabase
          .from('scenechat_accounts')
          .select('*')
          .eq('handle', handle)
          .maybeSingle();

        if (error) throw error;
        if (!row) return false;

        const { data: suggestionRows } = await supabase
          .from('scenechat_suggestions')
          .select('*')
          .eq('account_id', row.id)
          .order('created_at', { ascending: false });

        const account = mapRowToAccount(row, suggestionRows || []);
        set({ currentAccount: account });
        return true;
      },

      logout: () => {
        get().unsubscribeFromSuggestions();
        set({ currentAccount: null, hasFollowed: false, hasSubmitted: false });
      },

      fetchAccountByHandle: async (handle: string) => {
        const { data: row, error } = await supabase
          .from('scenechat_accounts')
          .select('*')
          .eq('handle', handle)
          .maybeSingle();

        if (error) throw error;
        if (!row) return null;
        return mapRowToAccount(row);
      },

      updateAccount: async (accountId: string, updates: Partial<Account>) => {
        const dbUpdates: Record<string, any> = {};
        if (updates.displayName !== undefined) dbUpdates.display_name = updates.displayName;
        if (updates.instagramUrl !== undefined) dbUpdates.instagram_url = updates.instagramUrl;
        if (updates.handle !== undefined) dbUpdates.handle = updates.handle;
        if (updates.profileImageUrl !== undefined) dbUpdates.profile_image_url = updates.profileImageUrl;
        if (updates.qrCodePath !== undefined) dbUpdates.qr_code_path = updates.qrCodePath;
        if (updates.qrCodePublicUrl !== undefined) dbUpdates.qr_code_public_url = updates.qrCodePublicUrl;
        if (updates.publicPageUrl !== undefined) dbUpdates.public_page_url = updates.publicPageUrl;
        if (updates.closesAt !== undefined) dbUpdates.closes_at = updates.closesAt;

        const { error } = await supabase
          .from('scenechat_accounts')
          .update(dbUpdates)
          .eq('id', accountId);
        if (error) throw error;

        const { currentAccount } = get();
        if (currentAccount && currentAccount.id === accountId) {
          set({ currentAccount: { ...currentAccount, ...updates } });
        }
      },

      addSuggestion: async (accountId: string, message: string, submittedBy: string | null, source: 'qr' | 'direct', campaignId?: string, location?: string | null) => {
        // location-kolonnen er valgfri — ta den kun med hvis den er satt, slik at
        // insert fungerer både før og etter DB-migrasjonen som legger til kolonnen.
        const payload: Record<string, any> = {
          account_id: accountId,
          message,
          submitted_by: submittedBy || null,
          source,
          campaign_id: campaignId || null,
        };
        if (location) payload.location = location;

        const { error } = await supabase
          .from('scenechat_suggestions')
          .insert(payload);
        if (error) throw error;
        set({ hasSubmitted: true });
      },

      toggleUsed: async (suggestionId: string) => {
        const { currentAccount } = get();
        if (!currentAccount) return;

        const suggestion = currentAccount.suggestions.find(s => s.id === suggestionId);
        if (!suggestion) return;

        const { error } = await supabase
          .from('scenechat_suggestions')
          .update({ used: !suggestion.used })
          .eq('id', suggestionId);
        if (error) throw error;

        set({
          currentAccount: {
            ...currentAccount,
            suggestions: currentAccount.suggestions.map(s =>
              s.id === suggestionId ? { ...s, used: !s.used } : s
            ),
          },
        });
      },

      removeAllSuggestions: async () => {
        const { currentAccount } = get();
        if (!currentAccount) return;

        const { error } = await supabase
          .from('scenechat_suggestions')
          .delete()
          .eq('account_id', currentAccount.id);
        if (error) throw error;

        set({
          currentAccount: { ...currentAccount, suggestions: [] },
        });
      },

      refreshSuggestions: async () => {
        const { currentAccount } = get();
        if (!currentAccount) return;

        const { data: rows, error } = await supabase
          .from('scenechat_suggestions')
          .select('*')
          .eq('account_id', currentAccount.id)
          .order('created_at', { ascending: false });
        if (error) throw error;

        set({
          currentAccount: {
            ...currentAccount,
            suggestions: (rows || []).map(mapRowToSuggestion),
          },
        });
      },

      setHasFollowed: (value: boolean) => {
        set({ hasFollowed: value });
      },

      subscribeToSuggestions: () => {
        const { currentAccount } = get();
        if (!currentAccount) return;

        get().unsubscribeFromSuggestions();

        const channel = supabase
          .channel(`suggestions-${currentAccount.id}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'scenechat_suggestions',
              filter: `account_id=eq.${currentAccount.id}`,
            },
            () => {
              get().refreshSuggestions();
            }
          )
          .subscribe();

        set({ realtimeChannel: channel });
      },

      unsubscribeFromSuggestions: () => {
        const { realtimeChannel } = get();
        if (realtimeChannel) {
          supabase.removeChannel(realtimeChannel);
          set({ realtimeChannel: null });
        }
      },

      generateAndStoreQR: async (accountId: string) => {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

        const res = await fetch(`${supabaseUrl}/functions/v1/generate-qr`, {
          method: 'POST',
          headers: {
            'apikey': anonKey,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ accountId }),
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `Failed to generate QR code (${res.status})`);
        }

        const { publicPageUrl, qrCodePath, qrCodePublicUrl } = await res.json();

        const { currentAccount } = get();
        if (currentAccount && currentAccount.id === accountId) {
          set({
            currentAccount: {
              ...currentAccount,
              publicPageUrl,
              qrCodePath,
              qrCodePublicUrl,
            },
          });
        }
      },

      // --- Campaign actions ---

      fetchCampaign: async (handle: string, slug: string) => {
        const { data: accountRow, error: accErr } = await supabase
          .from('scenechat_accounts')
          .select('*')
          .eq('handle', handle)
          .maybeSingle();

        if (accErr || !accountRow) return null;

        const { data: campaignRow, error: campErr } = await supabase
          .from('scenechat_campaigns')
          .select('*')
          .eq('account_id', accountRow.id)
          .eq('slug', slug)
          .maybeSingle();

        if (campErr || !campaignRow) return null;

        return {
          campaign: mapRowToCampaign(campaignRow),
          account: mapRowToAccount(accountRow),
        };
      },

      fetchCampaignsByAccount: async (accountId: string) => {
        const { data: rows, error } = await supabase
          .from('scenechat_campaigns')
          .select('*')
          .eq('account_id', accountId)
          .order('created_at', { ascending: true });

        if (error) throw error;
        return (rows || []).map(mapRowToCampaign);
      },

      createCampaign: async (accountId: string, data) => {
        const { data: row, error } = await supabase
          .from('scenechat_campaigns')
          .insert({
            account_id: accountId,
            slug: data.slug,
            type: data.type,
            title: data.title,
            description: data.description || null,
            closes_at: data.closesAt || null,
          })
          .select()
          .single();

        if (error) throw error;
        return mapRowToCampaign(row);
      },

      updateCampaign: async (campaignId: string, updates) => {
        const dbUpdates: Record<string, any> = {};
        if (updates.title !== undefined) dbUpdates.title = updates.title;
        if (updates.description !== undefined) dbUpdates.description = updates.description;
        if (updates.closesAt !== undefined) dbUpdates.closes_at = updates.closesAt;
        if (updates.slug !== undefined) dbUpdates.slug = updates.slug;

        const { error } = await supabase
          .from('scenechat_campaigns')
          .update(dbUpdates)
          .eq('id', campaignId);

        if (error) throw error;
      },

      deleteCampaign: async (campaignId: string) => {
        const { error } = await supabase
          .from('scenechat_campaigns')
          .delete()
          .eq('id', campaignId);

        if (error) throw error;
      },

      fetchVoteOptions: async (campaignId: string) => {
        const { data: rows, error } = await supabase
          .from('scenechat_vote_options')
          .select('*')
          .eq('campaign_id', campaignId)
          .order('sort_order', { ascending: true });

        if (error) throw error;
        return (rows || []).map(mapRowToVoteOption);
      },

      saveVoteOptions: async (campaignId: string, labels: string[]) => {
        // Delete existing options then insert new ones
        await supabase
          .from('scenechat_vote_options')
          .delete()
          .eq('campaign_id', campaignId);

        if (labels.length === 0) return;

        const { error } = await supabase
          .from('scenechat_vote_options')
          .insert(
            labels.map((label, i) => ({
              campaign_id: campaignId,
              label,
              sort_order: i,
            }))
          );

        if (error) throw error;
      },

      fetchCampaignSuggestions: async (campaignId: string) => {
        const { data: rows, error } = await supabase
          .from('scenechat_suggestions')
          .select('*')
          .eq('campaign_id', campaignId)
          .order('created_at', { ascending: false });

        if (error) throw error;
        return (rows || []).map(mapRowToSuggestion);
      },

      deleteSuggestion: async (suggestionId: string) => {
        const { error } = await supabase
          .from('scenechat_suggestions')
          .delete()
          .eq('id', suggestionId);
        if (error) throw error;
      },

      deleteCampaignSuggestions: async (campaignId: string) => {
        const { error } = await supabase
          .from('scenechat_suggestions')
          .delete()
          .eq('campaign_id', campaignId);
        if (error) throw error;
      },

      toggleSuggestionUsed: async (suggestionId: string, currentUsed: boolean) => {
        const { error } = await supabase
          .from('scenechat_suggestions')
          .update({ used: !currentUsed })
          .eq('id', suggestionId);
        if (error) throw error;
      },
    }),
    {
      name: 'scenechat-store',
      partialize: (state) => ({
        currentAccount: state.currentAccount,
      }),
    }
  )
);
